import React, { useMemo, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  Copy,
  DownloadSimple,
  Eye,
  EyeSlash,
  ImageSquare,
  Images,
  MonitorPlay,
  Plus,
  Trash,
  Upload,
  VideoCamera,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import api, { getApiErrorMessage } from '../services/api';
import { resolveMediaUrl } from '../utils/media';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import InfoTip from './InfoTip';
import useEventTypeConfig from '../hooks/useEventTypeConfig';

export default function GuestExperienceMediaManager({
  event,
  onRefresh,
  galleryPhotos = [],
  allowGallerySelection = false,
  section = 'all',
}) {
  const eventTypeConfig = useEventTypeConfig(event);
  const [busy, setBusy] = useState('');
  const [galleryPickerOpen, setGalleryPickerOpen] = useState(false);
  const [sourceLabel, setSourceLabel] = useState('');
  const [sourceItineraryId, setSourceItineraryId] = useState('');
  const experience = event?.guest_experience || {};
  const wall = experience.photo_wall || {};
  const showHero = section === 'all' || section === 'hero';
  const showPhotoWall = section === 'all' || section === 'photo-wall';
  const heroMedia = useMemo(
    () => [...(experience.hero_media || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [experience.hero_media]
  );
  const qrSources = useMemo(
    () => [
      ...(wall.public_token ? [{
        id: 'main',
        label: 'Main venue',
        token: wall.public_token,
        permanent: true,
      }] : []),
      ...(wall.sources || []),
    ],
    [wall.public_token, wall.sources]
  );

  const refresh = async () => {
    if (onRefresh) await onRefresh();
  };

  const uploadCouplePhoto = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setBusy('couple');
      await api.post(`/events/${event.id}/guest-experience/couple-photo`, formData);
      await refresh();
      toast.success(eventTypeConfig.labels.heroPortraitUpdated);
    } catch (error) {
      toast.error(getApiErrorMessage(error, eventTypeConfig.labels.heroPortraitUploadError));
    } finally {
      setBusy('');
    }
  };

  const uploadHeroMedia = async (files) => {
    if (!files?.length) return;
    try {
      setBusy('hero');
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/events/${event.id}/guest-experience/hero-media`, formData);
      }
      await refresh();
      toast.success(`${files.length} slideshow item${files.length === 1 ? '' : 's'} uploaded`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not upload slideshow media'));
    } finally {
      setBusy('');
    }
  };

  const moveMedia = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= heroMedia.length) return;
    const reordered = [...heroMedia];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    try {
      setBusy('order');
      await api.put(`/events/${event.id}/guest-experience/hero-media/order`, {
        media_ids: reordered.map((item) => item.id),
      });
      await refresh();
    } catch (error) {
      toast.error('Could not reorder the slideshow');
    } finally {
      setBusy('');
    }
  };

  const addGalleryPhoto = async (photo) => {
    try {
      setBusy(`gallery-${photo.id}`);
      await api.post(`/events/${event.id}/guest-experience/hero-media/from-gallery`, {
        photo_id: photo.id,
      });
      await refresh();
      toast.success('Photo added to the guest banner');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not add this gallery photo');
    } finally {
      setBusy('');
    }
  };

  const toggleVisibility = async (item) => {
    try {
      setBusy(item.id);
      await api.put(`/events/${event.id}/guest-experience/hero-media/${item.id}/visibility`, {
        visible: item.visible === false,
      });
      await refresh();
    } catch (error) {
      toast.error('Could not update slideshow visibility');
    } finally {
      setBusy('');
    }
  };

  const removeMedia = async (item) => {
    try {
      setBusy(item.id);
      await api.delete(`/events/${event.id}/guest-experience/hero-media/${item.id}`);
      await refresh();
      toast.success('Slideshow item removed');
    } catch (error) {
      toast.error('Could not remove the slideshow item');
    } finally {
      setBusy('');
    }
  };

  const updateWallSetting = async (key, value) => {
    try {
      setBusy(key);
      await api.put(`/events/${event.id}/photo-wall/settings`, { [key]: value });
      await refresh();
    } catch (error) {
      toast.error('Could not update the photo wall');
    } finally {
      setBusy('');
    }
  };

  const createSource = async () => {
    if (!sourceLabel.trim()) {
      toast.error('Enter a QR location label');
      return;
    }
    try {
      setBusy('source');
      await api.post(`/events/${event.id}/photo-wall/sources`, {
        label: sourceLabel.trim(),
        location_label: sourceLabel.trim(),
        itinerary_item_id: sourceItineraryId || null,
      });
      setSourceLabel('');
      setSourceItineraryId('');
      await refresh();
      toast.success('QR location created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create the QR location');
    } finally {
      setBusy('');
    }
  };

  const removeSource = async (sourceId) => {
    try {
      setBusy(sourceId);
      await api.delete(`/events/${event.id}/photo-wall/sources/${sourceId}`);
      await refresh();
    } catch (error) {
      toast.error('Could not remove the QR location');
    } finally {
      setBusy('');
    }
  };

  const shareUrl = (token) => `${window.location.origin}/share/${token}`;
  const qrUrl = (token) => `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=16&data=${encodeURIComponent(shareUrl(token))}`;

  const copyLink = async (token) => {
    await navigator.clipboard.writeText(shareUrl(token));
    toast.success('Photo wall link copied');
  };

  const downloadArchive = async () => {
    try {
      setBusy('archive');
      const response = await api.get(`/events/${event.id}/photo-wall/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${event.name || 'event'}-photo-wall.zip`;
      anchor.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Could not create the photo archive');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="space-y-6">
      {showHero && (
        <Card className="border-wedding-gold/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-1 font-serif">
            Guest View Hero
            <InfoTip content={eventTypeConfig.labels.heroManagerDescription} label="About the guest view hero" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-5 md:grid-cols-[180px_1fr] md:items-center">
            <div className="aspect-[4/5] overflow-hidden rounded-2xl border bg-muted">
              <img
                src={resolveMediaUrl(experience.couple_photo_url) || '/assets/guest-couple-placeholder.png'}
                alt={eventTypeConfig.labels.heroPortraitAlt}
                className={`h-full w-full ${experience.couple_photo_url ? 'object-cover' : 'object-contain p-3'}`}
              />
            </div>
            <div>
              <div className="flex items-center gap-1">
                <Label>{eventTypeConfig.labels.heroPortraitLabel}</Label>
                <InfoTip content="Use a portrait image. It appears as a small inset over the slideshow." label={`About the ${eventTypeConfig.labels.heroPortraitLabel.toLowerCase()}`} />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button asChild disabled={busy === 'couple'}>
                  <label>
                    <Upload className="mr-2 h-4 w-4" /> {busy === 'couple' ? 'Uploading...' : 'Upload photo'}
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={(e) => uploadCouplePhoto(e.target.files?.[0])} />
                  </label>
                </Button>
                {experience.couple_photo_url && (
                  <Button
                    variant="outline"
                    onClick={async () => {
                      setBusy('couple');
                      try {
                        await api.delete(`/events/${event.id}/guest-experience/couple-photo`);
                        await refresh();
                      } finally {
                        setBusy('');
                      }
                    }}
                  >
                    <Trash className="mr-2 h-4 w-4" /> Remove
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="border-t pt-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-1">
                <Label>Hero slideshow</Label>
                <InfoTip content="Add up to 20 JPEG, PNG, WebP, MP4, WebM, or MOV files." label="About hero slideshow media" />
              </div>
              <div className="flex flex-wrap gap-2">
                {allowGallerySelection && (
                  <Button type="button" variant="outline" onClick={() => setGalleryPickerOpen(true)}>
                    <Images className="mr-2 h-4 w-4" /> Choose from All Photos
                  </Button>
                )}
                <Button asChild disabled={busy === 'hero'}>
                  <label>
                    <Plus className="mr-2 h-4 w-4" /> {busy === 'hero' ? 'Uploading...' : 'Add media'}
                    <input type="file" multiple accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime" className="sr-only" onChange={(e) => uploadHeroMedia(e.target.files)} />
                  </label>
                </Button>
              </div>
            </div>
            {heroMedia.length ? (
              <div className="mt-4 grid gap-3">
                {heroMedia.map((item, index) => (
                  <div key={item.id} className="flex items-center gap-3 rounded-xl border bg-white p-3">
                    <div className="flex h-16 w-24 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted">
                      {item.media_type === 'video' ? (
                        <video src={resolveMediaUrl(item.url)} className="h-full w-full object-cover" muted />
                      ) : (
                        <img src={resolveMediaUrl(item.url)} alt="" className="h-full w-full object-cover" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {item.media_type === 'video' ? <VideoCamera className="h-4 w-4" /> : <ImageSquare className="h-4 w-4" />}
                        <span className="text-sm font-medium">Slide {index + 1}</span>
                        {item.visible === false && <Badge variant="secondary">Hidden</Badge>}
                      </div>
                      <p className="mt-1 truncate text-xs text-muted-foreground">{item.caption || item.media_type}</p>
                    </div>
                    <div className="flex flex-wrap justify-end">
                      <Button variant="ghost" size="icon" disabled={index === 0 || busy === 'order'} onClick={() => moveMedia(index, -1)} aria-label="Move up"><ArrowUp className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" disabled={index === heroMedia.length - 1 || busy === 'order'} onClick={() => moveMedia(index, 1)} aria-label="Move down"><ArrowDown className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" disabled={busy === item.id} onClick={() => toggleVisibility(item)} aria-label="Toggle visibility">
                        {item.visible === false ? <Eye className="h-4 w-4" /> : <EyeSlash className="h-4 w-4" />}
                      </Button>
                      <Button variant="ghost" size="icon" disabled={busy === item.id} onClick={() => removeMedia(item)} className="text-red-600" aria-label="Remove slide"><Trash className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-4 rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">No slideshow media uploaded yet.</div>
            )}
          </div>
        </CardContent>
        </Card>
      )}

      {showPhotoWall && (
        <Card className="border-wedding-sage/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-1 font-serif">
            Live Photo Wall
            <InfoTip content="Guest uploads are automatically moderated. Approved images also appear in All Photos; there is no manual approval queue." label="About the live photo wall" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              ['enabled', 'Show photo wall', 'Display the wall in the guest view'],
              ['upload_enabled', 'Guest uploads', 'Allow new QR camera uploads'],
              ['projector_enabled', 'Venue projector', 'Allow the full-screen venue display'],
            ].map(([key, label, description]) => (
              <div key={key} className="flex items-start justify-between gap-3 rounded-xl border p-4">
                <div className="flex items-center gap-1">
                  <Label htmlFor={`wall-${key}`}>{label}</Label>
                  <InfoTip content={description} label={`About ${label}`} />
                </div>
                <Switch
                  id={`wall-${key}`}
                  checked={wall[key] !== false}
                  disabled={busy === key}
                  onCheckedChange={(value) => updateWallSetting(key, value)}
                />
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button asChild disabled={wall.projector_enabled === false}>
              <Link to="/photo-wall/projector" target="_blank">
                <MonitorPlay className="mr-2 h-4 w-4" /> Launch projector
              </Link>
            </Button>
            <Button variant="outline" onClick={downloadArchive} disabled={busy === 'archive'}>
              <DownloadSimple className="mr-2 h-4 w-4" /> {busy === 'archive' ? 'Preparing...' : 'Download keepsakes'}
            </Button>
          </div>

          <div className="border-t pt-5">
            <div className="flex items-center gap-1">
              <Label>QR locations</Label>
              <InfoTip content="Create one QR code per table, bar, hall, or itinerary event for automatic categorization." label="About QR locations" />
            </div>
            <div className="mt-3 grid gap-2 md:grid-cols-[1fr_1fr_auto]">
              <Input value={sourceLabel} onChange={(e) => setSourceLabel(e.target.value)} placeholder="e.g. Reception Bar" />
              <select value={sourceItineraryId} onChange={(e) => setSourceItineraryId(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm">
                <option value="">Auto-detect itinerary</option>
                {(event.itinerary || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
              <Button onClick={createSource} disabled={busy === 'source'}><Plus className="mr-2 h-4 w-4" /> Create QR</Button>
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {qrSources.map((source) => (
                <div key={source.id} className="rounded-xl border bg-white p-4">
                  <img src={qrUrl(source.token)} alt={`${source.label} QR code`} className="mx-auto h-40 w-40 border bg-white p-1" />
                  <p className="mt-3 text-center font-medium">{source.label}</p>
                  <p className="mt-1 truncate text-center text-xs text-muted-foreground">{shareUrl(source.token)}</p>
                  <div className="mt-3 flex justify-center gap-1">
                    <Button variant="outline" size="sm" onClick={() => copyLink(source.token)}><Copy className="mr-1 h-4 w-4" /> Copy</Button>
                    {!source.permanent && (
                      <Button variant="ghost" size="sm" className="text-red-600" disabled={busy === source.id} onClick={() => removeSource(source.id)}><Trash className="h-4 w-4" /></Button>
                    )}
                  </div>
                </div>
              ))}
              {!qrSources.length && (
                <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground sm:col-span-2 lg:col-span-3">
                  Create a QR location to start no-login guest uploads.
                </div>
              )}
            </div>
          </div>
        </CardContent>
        </Card>
      )}

      {showHero && (
        <Dialog open={galleryPickerOpen} onOpenChange={setGalleryPickerOpen}>
        <DialogContent className="max-h-[88vh] max-w-5xl overflow-hidden">
          <DialogHeader>
            <DialogTitle>Choose from All Photos</DialogTitle>
          </DialogHeader>
          <div className="max-h-[72vh] overflow-y-auto pr-1">
            {galleryPhotos.length ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                {galleryPhotos.map((photo) => {
                  const alreadyAdded = heroMedia.some(
                    (item) => item.source_photo_id === photo.id || item.url === photo.url
                  );
                  return (
                    <div key={photo.id} className="overflow-hidden rounded-xl border bg-white">
                      <img
                        src={resolveMediaUrl(photo.url, { preferProxy: true })}
                        alt={photo.caption || 'Gallery photo'}
                        className="aspect-square w-full object-cover"
                      />
                      <div className="p-2">
                        <Button
                          type="button"
                          size="sm"
                          className="w-full"
                          variant={alreadyAdded ? 'secondary' : 'default'}
                          disabled={alreadyAdded || busy === `gallery-${photo.id}` || heroMedia.length >= 20}
                          onClick={() => addGalleryPhoto(photo)}
                        >
                          {alreadyAdded ? 'Already added' : busy === `gallery-${photo.id}` ? 'Adding...' : 'Add to banner'}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed p-10 text-center text-sm text-muted-foreground">
                All Photos is empty. Upload gallery photos first.
              </div>
            )}
          </div>
        </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
